import {
  deleteExecutionResultByExecutionIdUrl,
  deleteResearchQueryByResearchQueryIdUrl,
  getGenericResearchQueriesList,
  getGenericResearchQueryDetailByIdURL,
  getGenericResearchQueryURL,
  getResearchQueryByExecutionIdUrl,
  getResearchQueryHistorySummaryURL,
  abortResearchQueryExecutionbyIdUrl,
  cloneGenericResearchQueryUrl,
  getGenericResearchQueryBackupUrl,
  validateImportedGenericResearchQueryUrl,
  reactivateGenericResearchQueryUrl,
} from "./constants";
import base from "../middlewares/interceptors";
import { IGetGenericResearchQueriesProp } from "../types/ResearchQueries";

export interface IResearchQueryHistoryData {
  page: number;
  pSize: number;
  domainId?: number | null;
  ruleId?: number | null;
  fromDate?: string | undefined;
  searchFilterData: any;
}

export const getResearchQueriesList = async ({
  page,
  pSize,
  isActive = "true",
  genericResearchQueryId,
  isScoreConfigAssociated,
}: IGetGenericResearchQueriesProp) => {
  try {
    const pageN = page == null ? process.env.REACT_APP_DEFAULT_PAGE : page;
    const pageS =
      pSize == null ? process.env.REACT_APP_DEFAULT_PAGE_SIZE : pSize;

    // Build base URL with pagination parameters
    let requestUrl = `${getGenericResearchQueriesList}/pages?page=${pageN}&size=${pageS}`;

    // Conditionally add is_active parameter only when not "all"
    // When isActive is "all" or empty, omit the parameter to fetch all queries
    if (isActive && isActive !== "all") {
      requestUrl += `&is_active=${isActive}`;
    }

    // Add generic research query ID filter parameter if provided
    if (genericResearchQueryId) {
      requestUrl += `&generic_research_queries_filter_ids=${genericResearchQueryId}`;
    }

    // Optional filter for queries that already have / do not have an
    // associated research-query-scoring-config. Used by the scoring add page
    // to hide queries that are already configured.
    if (typeof isScoreConfigAssociated === "boolean") {
      requestUrl += `&is_score_config_associated=${isScoreConfigAssociated}`;
    }

    const response = await base.get(requestUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const addGenericResearchQuery = async (reqBody: any) => {
  try {
    const response = await base.post(getGenericResearchQueryURL, reqBody);
    return response.data;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
};

export const getGenericResearchQueryDetail = async (
  currentResearchQueryId: number,
) => {
  try {
    const response = await base.get(
      `${getGenericResearchQueryDetailByIdURL}/${currentResearchQueryId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const updateGenericResearchQuery = async ({
  currentResearchQueryId,
  payload,
}: any) => {
  try {
    // Extract comment from payload to use as query parameter
    const comment = payload?.generic_research_query?.comment?.trim();

    // Remove comment from the payload body
    const { generic_research_query, ...restPayload } = payload;
    const { comment: _, ...rqWithoutComment } = generic_research_query || {};
    const payloadWithoutComment = {
      ...restPayload,
      generic_research_query: rqWithoutComment,
    };

    // Build request URL with comment as query parameter if present
    const requestUrl = comment
      ? `${getGenericResearchQueryURL}/${currentResearchQueryId}?comment=${encodeURIComponent(
          comment,
        )}`
      : `${getGenericResearchQueryURL}/${currentResearchQueryId}`;

    const response = await base.put(requestUrl, payloadWithoutComment);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

const RESEARCH_QUERY_HISTORY_LISTING_FIELDS = [
  "id",
  "run_id",
  "run_name",
  "execution_sub_group_id",
  "execution_sub_group_code",
  "generic_research_query_id",
  "generic_research_query_code",
  "execution_name",
  "execution_time",
  "job_status",
  "execution_mode",
  "total_time",
  "prev_execution_id",
  "output_logs",
  "score",
  "score_calculated_on",
].join(",");

export const getResearchQueryHistory = async ({
  page,
  pSize,
  domainId,
  ruleId,
  fromDate,
  searchFilterData,
}: IResearchQueryHistoryData) => {
  try {
    const pageN = page == null ? process.env.REACT_APP_DEFAULT_PAGE : page;
    const pageS =
      pSize == null ? process.env.REACT_APP_DEFAULT_PAGE_SIZE : pSize;
    const allFilterData = {
      ...searchFilterData,
      ...(domainId && { domain_id: domainId }),
      ...(ruleId && { rule_id: ruleId }),
      ...(fromDate && { date: fromDate }),
    };
    const params = new URLSearchParams({
      page: pageN,
      size: pageS,
      fields: RESEARCH_QUERY_HISTORY_LISTING_FIELDS,
      ...Object.entries(allFilterData || {}).reduce(
        (acc: any, [key, value]: any) => {
          if (value !== null && value !== undefined && value !== "") {
            // Transform field names to match API parameter names
            const paramKey =
              key === "generic_execution_id"
                ? "ids"
                : key === "generic_research_code"
                  ? "generic_research_query_code"
                  : key === "start_date"
                    ? "date"
                    : key;
            acc[paramKey] = value;
          }
          return acc;
        },
        {},
      ),
    });
    const response = await base.get(
      `${getResearchQueryHistorySummaryURL}?${params.toString()}`,
    );
    return response?.data;
  } catch (error) {
    console.error(error);
  }
};

export const getResearchQueryByExecutionId = async ({
  currentResearchQueryResultId,
}: any) => {
  try {
    let requestUrl;
    if (currentResearchQueryResultId && currentResearchQueryResultId !== 0) {
      requestUrl = `${getResearchQueryByExecutionIdUrl}/${currentResearchQueryResultId}`;
    }
    const response = await base.get(requestUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};
export const deleteGenericResearchQueryById = async (
  currentExecutionId: any,
) => {
  try {
    const response = await base.delete(
      `${deleteResearchQueryByResearchQueryIdUrl}/${currentExecutionId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const deleteExecutionResultByExecutionId = async (
  currentExecutionId: any,
) => {
  try {
    const response = await base.delete(
      `${deleteExecutionResultByExecutionIdUrl}/${currentExecutionId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const abortResearchQueryExecution = async (currentExecutionId: any) => {
  try {
    const response = await base.post(
      `${abortResearchQueryExecutionbyIdUrl}/${currentExecutionId}`,
    );
    return response;
  } catch (error) {
    console.error(error);
  }
};

export const cloneGenericResearchQuery = async (reqBody: any) => {
  try {
    const response = await base.post(
      `${cloneGenericResearchQueryUrl}/${reqBody.generic_research_query_id}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getGenericResearchQueryBackup = async ({
  genericResearchQueryId,
  page,
  pSize,
}: any) => {
  try {
    const pageN = page == null ? process.env.REACT_APP_DEFAULT_PAGE : page;
    const pageS =
      pSize == null ? process.env.REACT_APP_DEFAULT_PAGE_SIZE : pSize;

    let requestUrl;
    if (genericResearchQueryId && genericResearchQueryId !== 0) {
      requestUrl = `${getGenericResearchQueryBackupUrl}?generic_research_queries_backup_filter_ids=${genericResearchQueryId}&page=${pageN}&size=${pageS}`;
    }
    const response = await base.get(requestUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const ValidateImportedGenericResearchQuery = async (reqBody: any) => {
  try {
    const response = await base.post(
      `${validateImportedGenericResearchQueryUrl}/${reqBody?.entity_description?.id}`,
      reqBody,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

/**
 * Reactivate generic research query
 * @param genericResearchQueryId - The ID of the generic research query to reactivate
 * @returns Promise with reactivation result
 */
export const reactivateGenericResearchQuery = async (
  genericResearchQueryId: string | number,
) => {
  try {
    const response = await base.put(
      `${reactivateGenericResearchQueryUrl}/${genericResearchQueryId}/reactivate`,
    );
    return response.data;
  } catch (error) {
    console.error("Error reactivating generic research query:", error);
    throw error;
  }
};
